import { ApplicantRoleLabel, toMoney } from "shared/utils";
import { MetricBreakdownColumn } from "../../types";
import { ApplicantMetricContribution } from "api/applications";
import { ReactNode } from "react";
import { Button, ButtonSize } from "shared/components";
import { useNavigate } from "react-router-dom";

const IncomeColumnLabel = {
  [MetricBreakdownColumn.NetIncome]: "Net income",
  [MetricBreakdownColumn.GrossIncome]: "Gross income",
  [MetricBreakdownColumn.MonthlyDebt]: "Monthly debt",
  [MetricBreakdownColumn.SelfAttestedGrossIncome]: "Self-attested gross income",
  [MetricBreakdownColumn.VerifiedGrossIncome]: "Verified gross income",
  [MetricBreakdownColumn.VerifiedNetIncome]: "Verified net income"
};

const Title = ({ children }: { children: ReactNode }) => (
  <p className="text-sm font-bold text-content-700 mb-[5px]">{children}</p>
);

const Subtitle = ({ children }: { children: ReactNode }) => (
  <p className="text-xs font-medium text-content-300">{children}</p>
);

const NavigateButton = ({
  url,
  children
}: {
  url: string;
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  return (
    <Button
      onClick={() => navigate(url)}
      size={ButtonSize.XS}
      className="!px-3 !mr-0"
    >
      {children}
    </Button>
  );
};

export const MetricCalculationColumn = ({
  columnType,
  applicantData
}: {
  columnType: MetricBreakdownColumn;
  applicantData: ApplicantMetricContribution;
}) => {
  switch (columnType) {
    case MetricBreakdownColumn.User:
      return (
        <>
          <Title>{applicantData.name}</Title>
          <Subtitle>{ApplicantRoleLabel[applicantData.role]}</Subtitle>
        </>
      );
    case MetricBreakdownColumn.MonthlyDebt:
      return (
        <>
          <Title>{toMoney(applicantData.debt ?? 0)}</Title>
          <Subtitle>{IncomeColumnLabel[columnType]}</Subtitle>
        </>
      );
    case MetricBreakdownColumn.NetIncome:
    case MetricBreakdownColumn.GrossIncome:
    case MetricBreakdownColumn.SelfAttestedGrossIncome:
    case MetricBreakdownColumn.VerifiedGrossIncome:
    case MetricBreakdownColumn.VerifiedNetIncome:
      return (
        <>
          <Title>{toMoney(applicantData.value)}</Title>
          <Subtitle>{IncomeColumnLabel[columnType]}</Subtitle>
        </>
      );
    case MetricBreakdownColumn.VerifiedBadge:
      return (
        <div className="w-16">
          <div className="ml-auto bg-success-50 text-success-700 rounded-full w-fit font-medium text-xs px-2 py-0.5">
            Verified
          </div>
        </div>
      );
    case MetricBreakdownColumn.SelfReportedBadge:
      return (
        <div className="w-24">
          <div className="ml-auto bg-alpha-40 text-content-700 rounded-full w-fit font-medium text-xs px-2 py-0.5">
            Self-reported
          </div>
        </div>
      );
    case MetricBreakdownColumn.CreditScore:
      return (
        <>
          <Title>{applicantData.value || "-"}</Title>
          <Subtitle>Credit score</Subtitle>
        </>
      );
    case MetricBreakdownColumn.ViewCreditReport:
      return (
        <NavigateButton
          url={`reports?applicantId=${applicantData.applicant_id}&report=credit_report`}
        >
          View report
        </NavigateButton>
      );
    case MetricBreakdownColumn.ViewApplication:
      return (
        <NavigateButton
          url={`reports?applicantId=${applicantData.applicant_id}`}
        >
          View application
        </NavigateButton>
      );
  }
};
